import React, { useState, useEffect, useRef } from 'react';
import { Button, Spinner, Alert, Form } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

import Constants from '../Constants';
// import Utils from '../utils/AppUtils';

import { useAuth } from '../AuthContext';
import { find_feature, has_feature, is_mobile_view } from '../utils/AppUtils';
import PersonaImages from '../utils/PersonaImages';
import ReviewModal from './ReviewModal';
import '../App.css';

const CALL_ENDED_MARKER = '[CALL_ENDED]';
const CALL_LOST_MARKER = '[CALL_LOST]';

const load_session = async (session_id, token, set_session, set_messages, set_template, set_call_ended, set_call_lost, set_loading, set_error, set_active_session_id, active_session_id_ref) => {
	if(!token) {
		return;
	}
	set_loading(true);
	set_error('');

	try {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		const response = await axios.get(Constants.SERVER_URL + '/api/call/session/' + session_id, { headers });
		const messages = response.data.messages || [];
		const is_ended = messages.some(m => m.content && m.content.includes(CALL_ENDED_MARKER));
		const is_lost = !is_ended && messages.some(m => m.content && m.content.includes(CALL_LOST_MARKER));

		set_session(response.data.session);
		set_messages(messages);
		set_template(response.data.template);
		set_call_ended(is_ended);
		set_call_lost(is_lost);
		set_active_session_id(session_id);
		active_session_id_ref.current = session_id;
	} catch (error) {
		set_error('Failed to load call session.');
	} finally {
		set_loading(false);
	}
};


const send_message = async (session_id, content, token, messages, set_messages, set_sending, set_call_ended, set_call_lost, set_error) => {
	set_sending(true);
	set_error('');

	const optimistic_msg = { role: 'user', content, sequence_number: messages.length + 1 };
	set_messages(prev => [...prev, optimistic_msg]);

	try {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};

		const response = await axios.post(Constants.SERVER_URL + '/api/call/message', {
			session_id,
			content,
		}, { headers });

		const assistant_msg = {
			role: 'assistant',
			content: response.data.reply,
			sequence_number: response.data.sequence_number,
		};
		set_messages(prev => [...prev, assistant_msg]);

		if(response.data.call_ended) {
			set_call_ended(true);
		}

		if(response.data.call_lost) {
			set_call_lost(true);
		}
	} catch (error) {
		set_messages(prev => prev.filter(m => {
			return m !== optimistic_msg;
		}));
		if(error.response && error.response.status === 403) {
			set_error('Quota exhausted. Please upgrade your plan to continue training.');
		} else {
			set_error('Failed to send message.');
		}
	} finally {
		set_sending(false);
	}
};

const strip_role_prefix = (content) => {
	if(!content) {
		return content;
	}

	if(content.startsWith('User: ')) {
		return content.substring(6);
	}

	return content;
};

const clean_content = (content) => {
	if(!content) {
		return content;
	}

	return strip_role_prefix(content).replace(CALL_ENDED_MARKER, '').replace(CALL_LOST_MARKER, '').trimEnd();
};

const render_message = (msg) => {
	const content = clean_content(msg.content);

	return (
		<div key={msg.sequence_number} className={'call-message-row call-message-row-' + msg.role}>
			<div className={'call-message call-message-' + msg.role}>
				{content}
			</div>
		</div>
	);
};

// The call carried on without a microphone. Warning rather than danger, and dismissible, because
// nothing has gone wrong with the call: it is running in text mode and the user may not care why.
const render_mic_notice = (mic_notice, on_dismiss) => {
	if(!mic_notice) {
		return null;
	}

	return (
		<Alert variant="warning" className="mx-3 mt-2" dismissible onClose={on_dismiss}>
			{mic_notice}
		</Alert>
	);
};

/* This code was generated by CallGPT */
const render_error_alert = (error, navigate) => {
	if(!error) {
		return null;
	}

	const is_quota_error = (error.indexOf('Quota') !== -1) || (error.indexOf('credits') !== -1);

	return (
		<Alert variant="danger" className="mx-3 mt-2">
			{error}
			{is_quota_error ? (
				<div style={styles.quota_action_area}>
					<Button
						size="sm"
						variant="outline-danger"
						onClick={() => {
							return navigate('/plans');
						}}>
						View Plans
					</Button>
				</div>
			) : null}
		</Alert>
	);
};

const render_typing_indicator = (role = 'assistant') => {
	return (
		<div className={'call-message-row call-message-row-' + role}>
			<div className={'call-message call-message-' + role + ' call-message-typing'}>
				<span className="call-typing-dot"></span>
				<span className="call-typing-dot"></span>
				<span className="call-typing-dot"></span>
			</div>
		</div>
	);
};

const SilenceThreshold = 0.02;
const SilenceDurationMs = 1500;
const SilencePollMs = 100;
const MinSpeechDurationMs = 200;

function CallView() {
	const is_mobile = is_mobile_view();
	// Device emulation sends an iOS userAgent, but the harness runs in emulated
	// Chromium where iOS autoplay restrictions do not exist, so the iOS tap
	// gesture must not engage in harness mode.
	const is_ios = (localStorage.getItem(Constants.HARNESS_MODE) !== 'true') &&
		(/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

	var { token, subscription_features, is_superadmin } = useAuth();
// is_superadmin = false;

	const { session_id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();

	const user_id = localStorage.getItem(Constants.USER_ID);
	const coach_review_feature = find_feature(Constants.c_FeatureCodes.AI_COACH_REVIEW, subscription_features);
	const has_coach_review = has_feature(coach_review_feature, is_superadmin);

	const is_new_session = !!(location.state && location.state.template);
	const [active_session_id, set_active_session_id] = useState(is_new_session ? null : session_id);
	const [session, set_session] = useState(null);
	const [sales_profile, set_sales_profile] = useState(null);
	const [messages, set_messages] = useState([]);
	const [template, set_template] = useState(null);
	const [loading, set_loading] = useState(false);
	const [sending, set_sending] = useState(false);
	const [transcribing, set_transcribing] = useState(false);
	const [call_ended, set_call_ended] = useState(false);
	const [call_lost, set_call_lost] = useState(false);
	const [show_review, set_show_review] = useState(false);
	const [review, set_review] = useState(null);
	const [review_loading, set_review_loading] = useState(false);
	const [error, set_error] = useState('');
	const [input, set_input] = useState('');
	const [copied, set_copied] = useState(false);
	const [voice_mode, set_voice_mode] = useState(
		localStorage.getItem(Constants.DEFAULT_VOICE_MODE) !== 'false'
	);
	const [recording, set_recording] = useState(false);

	// Said once when the call falls back to text because no microphone could be opened. Dismissible,
	// and not an error: the call is running normally, just without voice.
	const [mic_notice, set_mic_notice] = useState('');

	const [ios_tap_needed, set_ios_tap_needed] = useState(false);

	const messages_end_ref = useRef(null);
	const input_ref = useRef(null);
	const media_recorder_ref = useRef(null);
	const audio_chunks_ref = useRef([]);
	const audio_context_ref = useRef(null);
	const playback_context_ref = useRef(null);
	const analyser_interval_ref = useRef(null);
	const silence_timer_ref = useRef(null);
	const has_spoken_ref = useRef(false);
	const speaking_start_ref = useRef(null);
	const recording_cancelled_ref = useRef(false);
	const recording_started_ref = useRef(false);
	const voice_mode_ref = useRef(localStorage.getItem(Constants.DEFAULT_VOICE_MODE) !== 'false');
	const active_session_id_ref = useRef(is_new_session ? null : session_id);
	const audio_ref = useRef(null);
	const is_new_session_ref = useRef(is_new_session);
	const initial_audio_played_ref = useRef(false);
	const pending_audio_ref = useRef(null);
	const user_sent_message_ref = useRef(false);
	const token_ref = useRef(token);
	const is_mounted_ref = useRef(true);
	const abort_controller_ref = useRef(null);
	const session_loaded_for_id_ref = useRef(null);
	const harness_mode_ref = useRef(localStorage.getItem(Constants.HARNESS_MODE) === 'true');
	const harness_turns_ref = useRef(null);
	const harness_turn_index_ref = useRef(0);
	const harness_started_ref = useRef(false);

	useEffect(() => { token_ref.current = token; }, [token]);
	useEffect(() => { voice_mode_ref.current = voice_mode; }, [voice_mode]);

	useEffect(() => {
		if(!token || session_loaded_for_id_ref.current === session_id) {
			return;
		}
		session_loaded_for_id_ref.current = session_id;
		load_session(session_id, token, set_session, set_messages, set_template, set_call_ended, set_call_lost, set_loading, set_error, set_active_session_id, active_session_id_ref);
	}, [session_id, token]);

	useEffect(() => {
		if(loading || initial_audio_played_ref.current) {
			return;
		}

		const audio_base64 = location.state ? location.state.audio_base64 : null;
		const audio_format = location.state ? location.state.audio_format : null;
		if(!audio_base64 || !voice_mode_ref.current || !is_mounted_ref.current) {
			return;
		}

		initial_audio_played_ref.current = true;
		recording_started_ref.current = true;

		if(is_ios) {
			pending_audio_ref.current = audio_base64;
			set_ios_tap_needed(true);
			return;
		}

		if(harness_mode_ref.current) {
			log_harness_audio('greeting', audio_base64, audio_format);
			window.act_harness_audio_playing = true;
		}

		play_audio_base64(audio_base64, function() {
			if(harness_mode_ref.current) {
				window.act_harness_audio_playing = false;
				return;
			}

			if(voice_mode_ref.current && is_mounted_ref.current) {
				start_recording();
			}
		});

	}, [loading]);

	useEffect(() => {
		if(messages_end_ref.current) {
			messages_end_ref.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [messages, sending]);

	useEffect(() => {
		if(!sending && !call_ended && !call_lost && input_ref.current) {
			input_ref.current.focus();
		}
	}, [sending]);

	useEffect(() => {
		is_mounted_ref.current = true;

		return () => {
			is_mounted_ref.current = false;
			initial_audio_played_ref.current = false;
			recording_started_ref.current = false;
			if(abort_controller_ref.current) {
				abort_controller_ref.current.abort();
			}

			voice_mode_ref.current = false;
			recording_cancelled_ref.current = true;
			if(analyser_interval_ref.current) {
				clearInterval(analyser_interval_ref.current);
			}

			if(audio_context_ref.current) {
				audio_context_ref.current.close();
			}

			if(media_recorder_ref.current) {
				try {
					media_recorder_ref.current.stop();
				} catch(error) {}
			}

			if(audio_ref.current) {
				try { audio_ref.current.stop(); } catch(error) {}
				audio_ref.current = null;
			}

			if(playback_context_ref.current) {
				playback_context_ref.current.close();
				playback_context_ref.current = null;
			}
			
			if(is_new_session_ref.current && active_session_id_ref.current && !user_sent_message_ref.current) {
				const t = token_ref.current;
				const headers = t ? { Authorization: 'Bearer ' + t } : {};
				axios.delete(Constants.SERVER_URL + '/api/call/session/' + active_session_id_ref.current, { headers });
			}
		};
	}, []);

	const build_transcript = () => {
		const agent_label = template ? template.name : 'Agent';

		return messages.map(m => {
			const label = m.role === 'user' ? 'User' : agent_label;
			return label + ': ' + clean_content(m.content);
		}).join('\n\n');
	};

	const copy_to_clipboard = () => {
		navigator.clipboard.writeText(build_transcript()).then(() => {
			set_copied(true);
			setTimeout(() => set_copied(false), 2000);
		});
	};

	const download_transcript = () => {
		const text = build_transcript();
		const raw_name = (session && session.name) ? strip_role_prefix(session.name) : (template ? template.name : 'transcript');
		const filename = raw_name.replace(/[/\\?%*:|"<>]/g, '-') + '.txt';
		const blob = new Blob([text], { type: 'text/plain' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');

		a.href = url;
		a.download = filename;
		a.click();

		URL.revokeObjectURL(url);
	};

	const open_review = async () => {
		set_show_review(true);

		if(review) {
			return;
		}

		set_review_loading(true);

		try {
			const headers = token ? { Authorization: 'Bearer ' + token } : {};
			const response = await axios.post(
				Constants.SERVER_URL + '/api/call/session/' + active_session_id + '/review',
				{},
				{ headers }
			);
			set_review(response.data.review);
		} catch (error) {
			set_error('Failed to load coach review.');
		} finally {
			set_review_loading(false);
		}
	};

	const base64_to_array_buffer = function(base64) {
		const binary = atob(base64);
		const bytes = new Uint8Array(binary.length);

		for(var i = 0; i < binary.length; i++) {
			bytes[i] = binary.charCodeAt(i);
		}

		return bytes.buffer;
	};

	const sleep = (milliseconds)=>{
		return new Promise(resolve => setTimeout(resolve, milliseconds));
	}

	const play_audio_base64 = function(audio_base64, on_ended) {
		let playback_context = playback_context_ref.current;
		if(!playback_context || playback_context.state === 'closed') {
			playback_context = new AudioContext();
			playback_context_ref.current = playback_context;
		}

		const array_buffer = base64_to_array_buffer(audio_base64);

		playback_context.decodeAudioData(array_buffer, function(decoded_buffer) {
			if(!is_mounted_ref.current || playback_context.state === 'closed') {
				console.log("play_audio_base64 - cannot play!");
				return;
			}

			console.log("play_audio_base64 - playing...");

			const source = playback_context.createBufferSource();
			source.buffer = decoded_buffer;
			source.connect(playback_context.destination);
			audio_ref.current = source;

			source.onended = function() {
				if(on_ended) {
					on_ended();
				}
			};

			playback_context.resume().then(function() {
				source.start();
			});
		}, function(error) {
			console.log('play_audio_base64 - decodeAudioData ERROR: ', error);
		});
	};

	const stop_silence_detection = () => {
		if(analyser_interval_ref.current) {
			clearInterval(analyser_interval_ref.current);
			analyser_interval_ref.current = null;
		}
		if(audio_context_ref.current && audio_context_ref.current.state !== 'closed') {
			audio_context_ref.current.close();
		}
		audio_context_ref.current = null;
		silence_timer_ref.current = null;
	};

	const handle_voice_send = async (audio_blob) => {
		set_sending(true);
		set_error('');

		var call_is_over = false;
		var had_error = false;
		var has_audio = false;

		try {
			const headers = token ? { Authorization: 'Bearer ' + token } : {};

			const session_id_to_use = active_session_id_ref.current;

			set_transcribing(true);
			const transcribe_response = await axios.post(
				Constants.SERVER_URL + '/api/transcribe',
				audio_blob,
				{
					headers: {
						...headers,
						'Content-Type': 'audio/webm',
					}
				}
			);
			set_transcribing(false);

			const transcription = transcribe_response.data.text;

			if(transcription && transcription.trim()) {
				user_sent_message_ref.current = true;
				set_messages(prev => [...prev, {
					role: 'user',
					content: transcription,
					sequence_number: Date.now(),
				}]);

				abort_controller_ref.current = new AbortController();
				const reply_response = await axios.post(
					Constants.SERVER_URL + '/api/call/voice_reply',
					{ session_id: session_id_to_use, transcription },
					{ headers, signal: abort_controller_ref.current.signal }
				);

				const data = reply_response.data;

				if(data.reply) {
					set_messages(prev => [...prev, {
						role: 'assistant',
						content: data.reply,
						sequence_number: data.sequence_number,
					}]);
				}

				if(data.call_ended) {
					set_call_ended(true);
					call_is_over = true;
				}

				if(data.call_lost) {
					set_call_lost(true);
					call_is_over = true;
				}

				if(data.audio_base64 && is_mounted_ref.current) {
					has_audio = true;

					if(audio_ref.current) {
						try { audio_ref.current.stop(); } catch(error) {}
						audio_ref.current = null;
					}

					play_audio_base64(data.audio_base64, function() {
						if(voice_mode_ref.current && !call_is_over && !had_error && is_mounted_ref.current) {
							start_recording();
						}
					});
				}
			}
		} catch (error) {
			if(axios.isCancel(error) || error.name === 'CanceledError' || !is_mounted_ref.current) {
				return;
			}
			had_error = true;
			if(error.response && error.response.status === 403) {
				set_error('Quota exhausted. Please upgrade your plan to continue training.');
			} else {
				set_error('Failed to send voice message.');
			}
		} finally {
			set_sending(false);
		}

		if(voice_mode_ref.current && !call_is_over && !had_error && !has_audio) {
			start_recording();
		}
	};

	const log_harness_audio = (label, audio_base64, audio_format) => {
		if(!window.act_harness_audio_log) {
			window.act_harness_audio_log = [];
		}

		window.act_harness_audio_log.push({
			label: label,
			played_at_ms: Date.now(),
			audio_format: audio_format ? audio_format : 'wav',
			audio_base64: audio_base64,
		});
	};

	const get_harness_turns = () => {
		if(harness_turns_ref.current) {
			return harness_turns_ref.current;
		}

		const raw_turns = localStorage.getItem(Constants.HARNESS_TURNS);

		if(!raw_turns) {
			harness_turns_ref.current = [];
			return harness_turns_ref.current;
		}

		try {
			const parsed_turns = JSON.parse(raw_turns);

			if(Array.isArray(parsed_turns)) {
				harness_turns_ref.current = parsed_turns;
			} else {
				harness_turns_ref.current = [];
			}
		} catch (error) {
			console.log('CallView/get_harness_turns - ERROR: ', error);
			harness_turns_ref.current = [];
		}

		return harness_turns_ref.current;
	};

	const handle_harness_send = async (harness_turn) => {
		const transcription = harness_turn.user;
		const harness_reply = harness_turn.assistant;

		set_sending(true);
		set_error('');

		var call_is_over = false;
		var has_audio = false;

		try {
			const headers = token ? { Authorization: 'Bearer ' + token } : {};
			const session_id_to_use = active_session_id_ref.current;

			user_sent_message_ref.current = true;
			set_messages(prev => [...prev, {
				role: 'user',
				content: transcription,
				sequence_number: Date.now(),
			}]);

			abort_controller_ref.current = new AbortController();
			const reply_response = await axios.post(
				Constants.SERVER_URL + '/api/call/voice_reply',
				{ session_id: session_id_to_use, transcription, is_test_harness: true, harness_reply },
				{ headers, signal: abort_controller_ref.current.signal }
			);

			const data = reply_response.data;

			if(data.reply) {
				set_messages(prev => [...prev, {
					role: 'assistant',
					content: data.reply,
					sequence_number: data.sequence_number,
				}]);
			}

			if(data.call_ended) {
				set_call_ended(true);
				call_is_over = true;
			}

			if(data.call_lost) {
				set_call_lost(true);
				call_is_over = true;
			}

			if(data.audio_base64 && is_mounted_ref.current) {
				has_audio = true;

				if(audio_ref.current) {
					try { audio_ref.current.stop(); } catch(error) {}
					audio_ref.current = null;
				}

				log_harness_audio('prospect', data.audio_base64, data.audio_format);
				window.act_harness_audio_playing = true;

				play_audio_base64(data.audio_base64, function() {
					window.act_harness_audio_playing = false;
				});
			}
		} catch (error) {
			if(axios.isCancel(error) || error.name === 'CanceledError' || !is_mounted_ref.current) {
				return;
			}

			console.log('CallView/handle_harness_send - ERROR: ', error);

			if(error.response && error.response.status === 403) {
				set_error('Quota exhausted. Please upgrade your plan to continue training.');
			} else {
				set_error('Failed to send harness message.');
			}

			return;
		} finally {
			set_sending(false);
		}

		if(!has_audio) {
			window.act_harness_audio_playing = false;
		}

		return call_is_over;
	};

	const send_next_harness_turn = async () => {
		if(!harness_mode_ref.current || !is_mounted_ref.current) {
			return;
		}

		const harness_turns = get_harness_turns();
		const turn_index = harness_turn_index_ref.current;

		if(turn_index >= harness_turns.length) {
			console.log('CallView/send_next_harness_turn - no turns remaining');
			return;
		}

		harness_turn_index_ref.current = turn_index + 1;
		await handle_harness_send(harness_turns[turn_index]);
	};

	const start_recording = () => {
		recording_started_ref.current = true;
		recording_cancelled_ref.current = false;
		has_spoken_ref.current = false;
		speaking_start_ref.current = null;

		navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
			const media_recorder = new MediaRecorder(stream);
			media_recorder_ref.current = media_recorder;
			audio_chunks_ref.current = [];

			const audio_context = new AudioContext();
			audio_context_ref.current = audio_context;
			const analyser = audio_context.createAnalyser();
			analyser.fftSize = 1024;
			const source = audio_context.createMediaStreamSource(stream);
			source.connect(analyser);

			const buffer = new Uint8Array(analyser.fftSize);

			analyser_interval_ref.current = setInterval(() => {
				analyser.getByteTimeDomainData(buffer);

				var sum = 0;
				for(var i = 0; i < buffer.length; i++) {
					var normalized = (buffer[i] - 128) / 128;
					sum += normalized * normalized;
				}
				var rms = Math.sqrt(sum / buffer.length);

				if(rms >= SilenceThreshold) {
					if(speaking_start_ref.current === null) {
						speaking_start_ref.current = Date.now();
					} else if(Date.now() - speaking_start_ref.current >= MinSpeechDurationMs) {
						has_spoken_ref.current = true;
					}
					silence_timer_ref.current = null;
				} else {
					speaking_start_ref.current = null;
				}

				if(!has_spoken_ref.current) {
					return;
				}

				if(rms < SilenceThreshold) {
					if(silence_timer_ref.current === null) {
						silence_timer_ref.current = Date.now();
					} else if(Date.now() - silence_timer_ref.current >= SilenceDurationMs) {
						stop_silence_detection();
						media_recorder.stop();
					}
				}
			}, SilencePollMs);

			media_recorder.ondataavailable = (e) => {
				if(e.data.size > 0) {
					audio_chunks_ref.current.push(e.data);
				}
			};

			media_recorder.onstop = () => {
				stop_silence_detection();
				stream.getTracks().forEach((track) => {
					return track.stop();
				});
				set_recording(false);
				if(!recording_cancelled_ref.current) {
					const audio_blob = new Blob(audio_chunks_ref.current, { type: 'audio/webm' });
					handle_voice_send(audio_blob);
				}
			};

			media_recorder.start();
			set_recording(true);
		}).catch((error) => {
			console.log('start_recording - microphone unavailable: ', error);

			// No microphone, or permission refused. The call itself is unaffected: it carries on in
			// text mode rather than stopping at an error the user cannot act on from here.
			//
			// Set as a notice rather than through set_error, because an error banner stays for the
			// rest of the session and reads as though the call had failed.
			recording_cancelled_ref.current = true;
			stop_silence_detection();

			voice_mode_ref.current = false;
			set_voice_mode(false);
			set_recording(false);

			set_mic_notice('No microphone available, so this call is in text mode. Type your replies below.');
		});
	};

	const handle_voice_btn_click = () => {
		if(voice_mode_ref.current) {
			recording_cancelled_ref.current = true;
			stop_silence_detection();
			if(media_recorder_ref.current && recording) {
				media_recorder_ref.current.stop();
			}
			voice_mode_ref.current = false;
			set_voice_mode(false);
			set_recording(false);
			return;
		}
		voice_mode_ref.current = true;
		set_voice_mode(true);
		start_recording();
	};

	const handle_ios_tap = () => {
		set_ios_tap_needed(false);
		const playback_context = new AudioContext();
		playback_context_ref.current = playback_context;
		playback_context.resume();

		const pending = pending_audio_ref.current;
		pending_audio_ref.current = null;

		if(pending) {
			play_audio_base64(pending, function() {
				if(voice_mode_ref.current && is_mounted_ref.current) {
					start_recording();
				}
			});
		} else {
			start_recording();
		}
	};

	const handle_send = () => {
		if(!input.trim() || sending || call_ended || call_lost || !active_session_id) {
			return;
		}
		const content = input.trim();
		set_input('');
		user_sent_message_ref.current = true;
		send_message(active_session_id, content, token, messages, set_messages, set_sending, set_call_ended, set_call_lost, set_error);
	};

	const handle_key_down = (e) => {
		if(e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handle_send();
		}
	};

	const is_voice_ready = (active_session_id !== null) && (template !== null) && (!loading) && (!sending) && (!call_ended) && (!call_lost);

	useEffect(() => {
		if(!harness_mode_ref.current) {
			return;
		}

		window.act_harness_send_next_turn = send_next_harness_turn;
		window.act_harness_turns_remaining = get_harness_turns().length - harness_turn_index_ref.current;
	});

	useEffect(() => {
		if(harness_mode_ref.current) {
			if(is_voice_ready && !harness_started_ref.current) {
				harness_started_ref.current = true;
				window.act_harness_ready = true;
			}
			return;
		}

		if(is_voice_ready && voice_mode_ref.current && !recording_started_ref.current) {
			if(is_ios) {
				recording_started_ref.current = true;
				set_ios_tap_needed(true);
				return;
			}
			start_recording();
		}
	}, [is_voice_ready]);

	if(loading) {
		return (
			<main className="call-main">
				<div className="call-loading"><Spinner animation="border" /></div>
			</main>
		);
	}

	const width = window.innerWidth;
	const back_btn_label = is_mobile ? "←" : "Back";

	return (
		<main className="call-main">
			<div className="call-header">
				<Button
					size="sm"
					variant="outline-secondary"
					className="call-back-btn"
					onClick={() => {
						if(is_new_session_ref.current && active_session_id_ref.current && !user_sent_message_ref.current) {
							const headers = token ? { Authorization: 'Bearer ' + token } : {};
							axios.delete(Constants.SERVER_URL + '/api/call/session/' + active_session_id_ref.current, { headers });
							is_new_session_ref.current = false;
						}
						navigate(-1);
					}}>
					{is_mobile ? <i className="fas fa-arrow-left"></i> : '← Back'}
				</Button>
				{template ? (
					<React.Fragment>
						{PersonaImages[template.business_type] && (
							<img
								src={PersonaImages[template.business_type]}
								alt={template.prospect_name}
								className="call-header-avatar"
							/>
						)}
						<span className="call-header-name">{(session && session.name) ? strip_role_prefix(session.name) : template.name}</span>
					</React.Fragment>
				) : null}
				{messages.length > 0 ? (
					<React.Fragment>
						<Button
							size="sm"
							variant="outline-secondary"
							className="call-copy-btn"
							onClick={copy_to_clipboard}>
							<i className={copied ? 'fas fa-check' : 'fas fa-copy'}></i>
						</Button>
						<Button
							size="sm"
							variant="outline-secondary"
							className="call-download-btn"
							onClick={download_transcript}>
							<i className="fas fa-download"></i>
						</Button>
					</React.Fragment>
				) : null}
			</div>
			{render_error_alert(error, navigate)}
			{render_mic_notice(mic_notice, function() { set_mic_notice(''); })}
			<div className="call-messages-area">
				{messages.map(msg => render_message(msg))}
				{sending ? render_typing_indicator(transcribing ? 'user' : 'assistant') : null}
				<div ref={messages_end_ref} />
			</div>
			{call_ended ? (
				<div className="call-win-banner">
					<span><i className="fas fa-trophy"></i> Win! They agreed to move forward.</span>
					<Button
						size="sm"
						variant="outline-success"
						className="call-review-btn"
						onClick={open_review}>
						<i className="fas fa-award"></i> Coach Review
					</Button>
				</div>
			) : null}
			{call_lost ? (
				<div className="call-lose-banner">
					<span><i className="fas fa-phone-slash" style={{color: '#4a90d9'}}></i> They hung up.</span>
					<Button
						size="sm"
						variant="outline-danger"
						className="call-review-btn"
						onClick={open_review}>
						<i className="fas fa-award"></i> Coach Review
					</Button>
				</div>
			) : null}
			<ReviewModal
				show={show_review}
				on_hide={() => set_show_review(false)}
				review={review}
				loading={review_loading}
				has_coach_review={has_coach_review}
			/>
			<div className="call-input-area">
				{is_voice_ready ? (
					<Button
						size="sm"
						variant="outline-secondary"
						className={'call-voice-toggle-btn' + (recording ? ' call-voice-recording' : '')}
						onClick={handle_voice_btn_click}
						title={voice_mode ? 'Switch to text input' : 'Switch to voice input'}>
						<i className={voice_mode ? 'fas fa-keyboard' : 'fas fa-microphone'}></i>
					</Button>
				) : null}
				{(voice_mode && is_voice_ready) ? (
					<div className="call-voice-record-area">
						{ios_tap_needed ? (
							<Button
								variant="primary"
								className="call-ios-tap-btn"
								onClick={handle_ios_tap}>
								Tap to Begin
							</Button>
						) : (
							<span className="call-voice-status">
								{sending ? 'Processing...' : ((recording || harness_mode_ref.current) ? 'Listening...' : 'Starting...')}
							</span>
						)}
					</div>
				) : (
					<React.Fragment>
						<Form.Control
							as="textarea"
							rows={2}
							ref={input_ref}
							className="call-input"
							placeholder={(call_ended || call_lost) ? 'Call ended.' : 'Type a message... (Enter to send, Shift+Enter for new line)'}
							value={input}
							onChange={e => set_input(e.target.value)}
							onKeyDown={handle_key_down}
							disabled={sending || call_ended || call_lost}
						/>

						<Button
							variant="primary"
							className="call-send-btn"
							onClick={handle_send}
							disabled={!input.trim() || sending || call_ended || call_lost}>
							{sending ? (
								<Spinner animation="border" size="sm" />
							) : is_mobile ? (
								<i className="fas fa-paper-plane"></i>
							) : (
								"Send"
							)}
						</Button>

					</React.Fragment>
				)}
			</div>
		</main>
	);
}


/*
		<Button
			variant="primary"
			className="call-send-btn"
			onClick={handle_send}
			disabled={!input.trim() || sending || call_ended || call_lost}>
			{sending ? <Spinner animation="border" size="sm" /> : send_label}
		</Button>
*/

const styles = {
	quota_action_area: {
		marginTop: '10px',
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center'
	}
};

export default CallView;
